import type {
	LtiPlatformStartResult,
	WorkspaceElsewhereStartResult,
} from "@elaso/shared/types/lti";
import { accessEndedFrom, accessEndedWords } from "./access-ended-copy";

export type LaunchTab = {
	opener: unknown;
	location: { href: string };
	close: () => void;
};

export type LaunchOutcome = { ok: true } | { ok: false; message: string };

export type StartRefusal = Extract<
	WorkspaceElsewhereStartResult,
	{ ok: false }
>["reason"];

export const START_REFUSED = {
	keys_unavailable:
		"ELASO can't sign launches right now, so nothing was opened. Try again in a moment.",
	not_registered:
		"This LTI Tool is no longer registered for this organization, so nothing was opened.",
	consent_required:
		"Your permission is needed before an account is made for you there, so nothing was opened.",
	standing_gone: null,
	entry_removed: null,
} satisfies Record<StartRefusal, string | null>;

const POPUP_BLOCKED =
	"Your browser blocked the new tab. Allow pop-ups for ELASO and try again.";

const openBlankTab = (): LaunchTab | null => window.open("", "_blank");

export async function launchInNewTab(
	start: () => Promise<LtiPlatformStartResult>,
	open: () => LaunchTab | null = openBlankTab
): Promise<LaunchOutcome> {
	const tab = open();
	if (!tab) return { ok: false, message: POPUP_BLOCKED };
	tab.opener = null;

	let result: LtiPlatformStartResult;
	try {
		result = await start();
	} catch (err) {
		tab.close();
		return {
			ok: false,
			message: err instanceof Error ? err.message : String(err),
		};
	}

	if (!result.ok) {
		tab.close();
		return {
			ok: false,
			message:
				START_REFUSED[result.reason] ??
				accessEndedWords(accessEndedFrom(result.reason), {
					workspace: null,
					homeOrganization: null,
				}),
		};
	}
	tab.location.href = result.initiationUrl;
	return { ok: true };
}
